import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { accentIconProps } from '@/components/accentIcon';
import { buttonClasses } from '@/components/Button';
import { isDesktopRuntime } from '@/desktop/desktopDetection';
import { API_BASE_URL } from '@/services/ApiService';
import { useT } from '@/i18n/LocaleProvider';
import {
  chooseDownload,
  detectPlatform,
  downloadLabel,
  installerUrl,
  type IDesktopDownload,
} from './desktopDownload';

/**
 * Get the desktop app for the deployment you are already looking at.
 *
 * ⛔ **Renders nothing unless there is a build this browser can actually run.** That is the whole of
 * its behaviour when things are missing, and it is deliberate: `docs/concept-website.md` §4.5 says a
 * download must not be offered before it can be honoured. An icon that 404s, or one that hands an
 * Intel Mac an Apple Silicon build, is worse than no icon — the person cannot tell either from a
 * working one until after they have tried it.
 *
 * It therefore stays absent when this is the desktop app itself, when the deployment publishes no
 * build for this platform, and when two builds match and nothing established which processor this
 * is. There is no loading state and no error state, because an icon that flickers into a row of
 * three settled controls costs more than it tells anyone.
 */
export function DesktopDownloadButton({
  variant = 'icon',
  rowClassName,
  accentPosition,
}: {
  /** F-0415 (AC-18.3): `row` is the phone More menu's form, the icon with its name beside it. */
  variant?: 'icon' | 'row';
  rowClassName?: string;
  /**
   * F-0579 ACC-4b: the row's place in the phone menu's account rows, which picks its frame stop in
   * Colour (`row` only; the icon is a ghost button, which takes the button's blue, ACC-6).
   */
  accentPosition?: number;
} = {}) {
  const t = useT();
  const [download, setDownload] = useState<IDesktopDownload | null>(null);

  useEffect(() => {
    // Nothing to offer someone who already has it, and no request worth making to find that out.
    if (isDesktopRuntime()) return undefined;

    let cancelled = false;
    // StrictMode mounts twice: the first mount's request is aborted, so its body is never left
    // unread (a mocked e2e never reached network idle with it dangling).
    const controller = new AbortController();

    void (async () => {
      try {
        // The architecture only from a source that knows it. The user-agent string does not: every
        // Apple Silicon Mac still calls itself `Intel Mac OS X`.
        let architecture: string | null = null;
        const agentData = navigator.userAgentData;
        if (agentData?.getHighEntropyValues) {
          const values = await agentData.getHighEntropyValues(['architecture']);
          architecture = values.architecture ?? null;
        }

        const response = await fetch(`${API_BASE_URL}/desktop/download`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!response.ok || cancelled) return;
        const body: unknown = await response.json();
        const downloads = Array.isArray((body as { downloads?: unknown })?.downloads)
          ? ((body as { downloads: IDesktopDownload[] }).downloads ?? [])
          : [];

        if (!cancelled) {
          setDownload(chooseDownload(downloads, detectPlatform(navigator.userAgent, architecture)));
        }
      } catch {
        // An AbortError is the cleanup below, not a failure. A deployment too old to have the route, or one that cannot reach its release directory.
        // Both mean "no download here", which is already what an absent icon says.
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  if (!download) return null;

  const label = downloadLabel(download, t);
  if (variant === 'row') {
    return (
      <a
        href={installerUrl(API_BASE_URL, download)}
        download={download.fileName}
        // The visible words are the name; which build it is goes in the tooltip.
        title={label}
        className={rowClassName}
        data-desktop-download-row=""
      >
        <Download
          className="h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400"
          aria-hidden="true"
          {...(accentPosition === undefined ? {} : accentIconProps(accentPosition))}
        />
        <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
          {t('navigation.desktopDownload.row')}
        </span>
      </a>
    );
  }
  return (
    <a
      href={installerUrl(API_BASE_URL, download)}
      // The browser names the file from Content-Disposition; this is the fallback and the hint that
      // this link is a file rather than a page.
      download={download.fileName}
      aria-label={label}
      title={label}
      // 32 wide, as every other icon of the sidebar's account box (F-0585 PSW-7).
      className={buttonClasses({ variant: 'ghost', size: 'sm', className: 'w-8 px-0' })}
    >
      <Download className="h-4 w-4" />
    </a>
  );
}
